import React, { useEffect } from "react";
import { map, isEmpty, filter, some } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "helpers/tixxt-router";
import publishAllVolatile from "helpers/files/publishAllVolatile";
import { useFetchBaseMembershipData } from "actions/memberships";
import { useQueryParams } from "helpers/useQueryParams";
import { useSendMessage } from "../api";
import { messageInitialValues } from "../message";
import { FormProvider, useForm } from "react-hook-form";
import TextInput from "components/shared/form/fields/TextInput";
import TextEditor from "components/shared/form/fields/TextEditor";
import FileUploadField from "components/shared/form/fields/FileUploadField";
import DirectoryPickerInput from "components/shared/form/fields/DirectoryPickerInput";
import Checkbox from "components/shared/form/fields/Checkbox";
import CheckboxFieldWrapper from "components/shared/form/fields/CheckboxFieldWrapper";
import FormActions from "components/shared/form/fields/FormActions";
import {
  currentMemberSelector,
  networkAdminSelector,
} from "selectors/environment";
import { State } from "../../../@types";

type FormData = {
  to?: { id: string; displayValue: string; type?: string }[];
  to_all_network_members?: boolean;
  subject?: string;
  content?: string;
  attachments?: {
    name: string;
    id: string;
    original_filename: string;
    size: number;
    download_url: string;
    extension: string;
  }[];
};

export default function MessageComposer({
  initialValues,
}: {
  initialValues?: ReturnType<typeof messageInitialValues>;
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAdmin = useSelector(networkAdminSelector);
  const params = useQueryParams();
  const membershipId = params.to as string | undefined;
  const currentMember = useSelector((state: State) =>
    currentMemberSelector(state),
  );
  const { data } = useFetchBaseMembershipData(membershipId);
  const { mutate: sendMessage } = useSendMessage({
    onSuccess: () => {
      navigate("/messages");
      toastr.success(I18n.t("js.messages.message_sent"));
    },
    onError: () => {
      toastr.error(I18n.t("js.messages.message_not_sent"));
    },
  });
  const methods = useForm<FormData>({ values: initialValues });
  const { watch, handleSubmit, setValue } = methods;

  useEffect(() => {
    if (membershipId && !isEmpty(membershipId)) {
      setValue("to", [
        {
          id: membershipId,
          displayValue: I18n.t("js.tasks.loading"),
          type: "member",
        },
      ]);
    }
  }, [membershipId]);

  useEffect(() => {
    if (!data) return;
    if (membershipId && !isEmpty(membershipId)) {
      setValue("to", [
        { id: membershipId, displayValue: data.name, type: "member" },
      ]);
    }
  }, [data, membershipId]);

  useEffect(() => {
    if (!window.isApp) {
      window.addEventListener("beforeunload", onUnload);

      return () => window.removeEventListener("beforeunload", onUnload);
    }
  }, []);

  function onUnload(e) {
    e.preventDefault();
  }

  async function submit(data: FormData) {
    let params = {
      ...data,
      to: map(
        filter(data.to, (recipient) => recipient.type !== "group"),
        "id",
      ),
      to_group: map(
        filter(data.to, (recipient) => recipient.type === "group"),
        "id",
      ),
    };

    if (!isEmpty(data.attachments)) {
      const fileAttachments = await publishAllVolatile({
        dispatch,
        values: { attachments: data.attachments },
        storageDirectory: "files",
      });
      params = { ...params, ...fileAttachments };
    }

    sendMessage(params, {});
  }

  const toAll = watch("to_all_network_members");
  const recipients = watch("to");
  const hasGroupRecipient = some(recipients, { type: "group" });

  return (
    <FormProvider {...methods}>
      <form className="form-horizontal" onSubmit={handleSubmit(submit)}>
        <CheckboxFieldWrapper
          hint={
            toAll
              ? I18n.t("js.messages.composer.warn_to_all_network_members")
              : hasGroupRecipient
                ? I18n.t("js.messages.composer.warn_to_all_group_members")
                : ""
          }
        >
          <div className="flex gap-2">
            {currentMember && (
              <img
                className="member-image w-10 h-10"
                src={currentMember.images["40x40@2"]}
                alt=""
                aria-hidden
              />
            )}
            <div className="flex flex-col gap-2 w-full">
              <DirectoryPickerInput
                containerClassName="!flex !gap-1"
                childClassName="w-full border-none !p-0"
                name="to"
                label={I18n.t("js.messages.composer.to")}
                disabled={toAll}
                query={{ only_my_admin_groups: !isAdmin }}
                multi
              >
                {isAdmin && !initialValues?.conversation_starter_id && (
                  <div className="mt-2">
                    <Checkbox
                      name="to_all_network_members"
                      label={I18n.t(
                        "js.messages.composer.to_all_network_members",
                      )}
                    />
                  </div>
                )}
              </DirectoryPickerInput>
            </div>
          </div>
        </CheckboxFieldWrapper>
        <TextInput
          name="subject"
          label={I18n.t("js.messages.composer.subject")}
          placeholder={I18n.t("js.messages.composer.subject")}
        />
        <TextEditor
          name="content"
          placeholder={I18n.t("js.messages.composer.content.placeholder")}
        />
        <FileUploadField
          label={I18n.t("js.messages.composer.attachments")}
          name="attachments"
          options={{ multiple: true }}
        />
        <FormActions
          saveText={I18n.t("js.messages.composer.send")}
          saveDisabled={
            some(watch("attachments"), { state: "uploading" }) ||
            (isEmpty(recipients) && !toAll) ||
            (isEmpty(watch("subject")) && isEmpty(watch("content")))
          }
          onCancel={() => navigate("/messages")}
        />
      </form>
    </FormProvider>
  );
}
